/**
 * ActingTenantProvider — holds the currently-selected MANAGED account (a cross-
 * account delegation the current user accepted) or null (= the user's OWN
 * account, today's behaviour).
 *
 * When an account is selected it:
 *  1. records the owner tenant id on the API client (`setActingTenantId`), so
 *     EVERY subsequent request carries `X-Acting-Tenant: <owner_tenant_id>`
 *     (the fetch client attaches it globally — see lib/api/client.ts); and
 *  2. persists the full account (label + granted permissions) in sessionStorage
 *     so a reload keeps the active scope and the banner/switcher can render
 *     without a re-fetch. sessionStorage (not localStorage) so the scope dies
 *     with the tab; `clearActingTenant()` wipes it on logout.
 *
 * The provider wraps the whole app (above RouterProvider), so the header
 * switcher, the persistent "vous agissez dans…" banner, and every routed page
 * read the same scope via `useActingTenant()`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ACTING_TENANT_INVALID_EVENT, getActingTenantId, setActingTenantId } from '@/lib/api/client';
import type { DelegatedAccount } from '@/lib/api/delegations';

const ACCOUNT_KEY = 'psn-acting-account';

/** Read the persisted managed account, reconciling it with the client header. */
function readPersisted(): DelegatedAccount | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(ACCOUNT_KEY);
  } catch {
    raw = null;
  }
  if (!raw) {
    // No persisted account → make sure the header is clear too (defensive: the
    // two stores could drift if one write failed).
    if (getActingTenantId()) setActingTenantId(null);
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as DelegatedAccount;
    if (!parsed?.owner_tenant_id) throw new Error('malformed');
    // Re-assert the header from the persisted account (survives a hard reload
    // where only the client module re-initialised).
    if (getActingTenantId() !== parsed.owner_tenant_id) {
      setActingTenantId(parsed.owner_tenant_id);
    }
    return parsed;
  } catch {
    try {
      sessionStorage.removeItem(ACCOUNT_KEY);
    } catch {
      /* ignore */
    }
    setActingTenantId(null);
    return null;
  }
}

/** Clear any active delegation scope (header + storage). Safe to call anytime,
 *  e.g. on logout — exported standalone so non-React code can call it. */
export function clearActingTenant(): void {
  setActingTenantId(null);
  try {
    sessionStorage.removeItem(ACCOUNT_KEY);
  } catch {
    /* ignore */
  }
}

interface ActingTenantContextValue {
  /** The managed account currently acted in, or null for the user's own account. */
  account: DelegatedAccount | null;
  /** True while acting inside a managed (delegated) account. */
  isActing: boolean;
  /** Select a managed account (or null to return to the own account). */
  setAccount: (account: DelegatedAccount | null) => void;
  /** Return to the user's own account. */
  clear: () => void;
}

const ActingTenantContext = createContext<ActingTenantContextValue | null>(null);

export function ActingTenantProvider({ children }: { children: ReactNode }) {
  const [account, setAccountState] = useState<DelegatedAccount | null>(() => readPersisted());

  const setAccount = useCallback((next: DelegatedAccount | null) => {
    if (next) {
      setActingTenantId(next.owner_tenant_id);
      try {
        sessionStorage.setItem(ACCOUNT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    } else {
      clearActingTenant();
    }
    setAccountState(next);
  }, []);

  const clear = useCallback(() => {
    clearActingTenant();
    setAccountState(null);
  }, []);

  // The API client drops a dead X-Acting-Tenant when the server rejects it
  // (grant revoked / expired); mirror that here and fall back to the own account.
  useEffect(() => {
    const onInvalid = () => {
      clearActingTenant();
      setAccountState(null);
      window.location.reload();
    };
    window.addEventListener(ACTING_TENANT_INVALID_EVENT, onInvalid);
    return () => window.removeEventListener(ACTING_TENANT_INVALID_EVENT, onInvalid);
  }, []);

  const value = useMemo<ActingTenantContextValue>(
    () => ({ account, isActing: account !== null, setAccount, clear }),
    [account, setAccount, clear],
  );

  return <ActingTenantContext.Provider value={value}>{children}</ActingTenantContext.Provider>;
}

/** Access the acting-tenant scope. Returns a safe default when used outside the
 *  provider (so a stray consumer never crashes). */
export function useActingTenant(): ActingTenantContextValue {
  const ctx = useContext(ActingTenantContext);
  if (ctx) return ctx;
  return {
    account: null,
    isActing: false,
    setAccount: () => undefined,
    clear: () => undefined,
  };
}
